import React, { useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import { Title, useNotify } from "react-admin";
import { apiUrl } from "../../dataProvider";

const MANDATORY_RING_FIELDS = [
  { key: "ring_material", label: "Ring Material" },
  { key: "profile", label: "Profile" },
  { key: "width", label: "Width" },
];

const OPTIONAL_RING_FIELDS = [
  { key: "weight", label: "Weight" },
  { key: "ring_finish", label: "Ring Finish" },
  { key: "second_ring_finish", label: "Second Ring Finish" },
  { key: "sleeve", label: "Sleeve" },
];

const ENGRAVING_FIELDS = [
  { key: "engraving_type", label: "Engraving Type", mandatory: true },
  { key: "engraving", label: "Engraving" },
  { key: "engraving_font", label: "Engraving Font" },
  { key: "engraving_text", label: "Engraving Text" },
];

const FEATURE_GROUP_FIELDS = [
  { key: "groove_group", label: "Groove Group" },
  { key: "inlay_material", label: "Inlay Material" },
  { key: "inlay_finish", label: "Inlay Finish" },
  { key: "inlay_accent", label: "Inlay Accent", aliases: ["mga"] },
  { key: "gemstone_arrangement", label: "Gemstone Arrangement" },
  { key: "gemstone_cut", label: "Gemstone Cut" },
  { key: "gemstone_setting", label: "Gemstone Setting" },
  { key: "gemstone_size", label: "Gemstone Size" },
  { key: "gemstone_material", label: "Gemstone Material" },
  { key: "machine_pattern", label: "Machine Pattern" },
  {
    key: "machine_pattern_cerakote_color",
    label: "Machine Pattern Cerakote Color",
    aliases: ["cerakote_color"],
  },
  { key: "all_cerakote_color", label: "All Cerakote Color", aliases: ["cerakote"] },
];

const hasValue = value => value != null && value !== "";

const getFieldValue = (data, field) => {
  if (!data) return null;
  if (hasValue(data[field.key])) return data[field.key];
  if (field.aliases) {
    for (const alias of field.aliases) {
      if (hasValue(data[alias])) return data[alias];
    }
  }
  return null;
};

const useStyles = makeStyles(theme => ({
  card: {
    margin: theme.spacing(2),
  },
  searchRow: {
    marginBottom: theme.spacing(3),
  },
  fieldLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: 4,
  },
  fieldValue: {
    fontWeight: 500,
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  groupCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  groupTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  emptyState: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
  },
}));

const FieldItem = ({ label, value, classes }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography className={classes.fieldLabel}>{label}</Typography>
    <Typography className={classes.fieldValue}>{value || "-"}</Typography>
  </Grid>
);

const FieldsGrid = ({ fields, data, classes, showAll = false }) => {
  const visibleFields = showAll
    ? fields
    : fields.filter(
        field => field.mandatory || hasValue(getFieldValue(data, field))
      );

  if (visibleFields.length === 0) return null;

  return (
    <Grid container spacing={2}>
      {visibleFields.map(field => (
        <FieldItem
          key={field.key}
          label={field.label}
          value={getFieldValue(data, field)}
          classes={classes}
        />
      ))}
    </Grid>
  );
};

const MagicNumberSearch = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [magicNumber, setMagicNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSearch = async event => {
    event.preventDefault();

    const trimmed = magicNumber.trim();
    if (!trimmed) {
      notify("Please enter a magic number", { type: "warning" });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const { data } = await axios.get(`${apiUrl}/ring/info`, {
        params: { magic_number: trimmed },
      });
      setResult(data.data || data);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.errors?.magic_number?.[0] ||
        "Unable to fetch ring info for this magic number";
      notify(message, { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  const featureGroups = Array.isArray(result?.feature_groups)
    ? result.feature_groups
    : [];

  const engravingData =
    result?.engraving && typeof result.engraving === "object"
      ? result.engraving
      : result;

  const hasEngravingSection =
    result?.engraving != null ||
    ENGRAVING_FIELDS.some(field => hasValue(getFieldValue(engravingData, field)));

  const visibleFeatureGroups = featureGroups
    .map((group, index) => ({
      index,
      fields: FEATURE_GROUP_FIELDS.filter(field => hasValue(getFieldValue(group, field))),
    }))
    .filter(group => group.fields.length > 0);

  return (
    <>
      <Title title="Magic Number Search" />
      <Card className={classes.card}>
        <CardContent>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} alignItems="center" className={classes.searchRow}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Magic Number"
                  value={magicNumber}
                  onChange={event => setMagicNumber(event.target.value)}
                  placeholder="e.g. A1B-2C3D"
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  disabled={loading}
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </form>

          {!result && !loading && (
            <Typography className={classes.emptyState}>
              Enter a magic number and click Search to view ring details.
            </Typography>
          )}

          {result && (
            <>
              <FieldsGrid
                fields={MANDATORY_RING_FIELDS}
                data={result}
                classes={classes}
                showAll
              />

              {OPTIONAL_RING_FIELDS.some(field => hasValue(getFieldValue(result, field))) && (
                <>
                  <Divider style={{ margin: "24px 0 16px" }} />
                  <FieldsGrid
                    fields={OPTIONAL_RING_FIELDS}
                    data={result}
                    classes={classes}
                  />
                </>
              )}

              {visibleFeatureGroups.length > 0 && (
                <>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Feature Groups
                  </Typography>
                  {visibleFeatureGroups.map(({ index, fields }) => (
                    <Paper
                      key={`feature-group-${index}`}
                      variant="outlined"
                      className={classes.groupCard}
                    >
                      <Typography variant="subtitle2" className={classes.groupTitle}>
                        Feature Group {index + 1}
                      </Typography>
                      <Grid container spacing={2}>
                        {fields.map(field => (
                          <FieldItem
                            key={field.key}
                            label={field.label}
                            value={getFieldValue(featureGroups[index], field)}
                            classes={classes}
                          />
                        ))}
                      </Grid>
                    </Paper>
                  ))}
                </>
              )}

              {hasEngravingSection && (
                <>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Engraving
                  </Typography>
                  <Paper variant="outlined" className={classes.groupCard}>
                    <FieldsGrid
                      fields={ENGRAVING_FIELDS}
                      data={engravingData}
                      classes={classes}
                    />
                  </Paper>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default MagicNumberSearch;
